// The module Dashboard page shell (BP-15, View 1100): the light-grey canvas and
// the `.an-home` content column, in one place.
//
// Aaron, 2026-09-30 (H277): "dashboards should have light grey backgrounds with
// white round panels … https://g2.aeronet.dev/app/job_costs/dashboard is correct
// pattern". Measured on that reference: the white rounded panels are the shared
// `.an-home .kpi / .card / .panel` rules (aeronet-home.css), but the grey canvas
// was a LOCAL `<Box sx={{ backgroundColor: "#fafbfb" }}>` in
// financial-reports/index.tsx (copied into SectionDashboard and the app home).
// Every dashboard that only wrapped itself in `<div className="an-home">`,
// Flight Expenses first, rendered white panels on the white app background.
//
// So a dashboard never renders `an-home` itself. It renders
//
//   <DashboardPage>…hero / tabs / KPIs / two-col / panel…</DashboardPage>
//
// and its loading and error early-returns render inside the same shell
// (`<DashboardPage><div className="empty">Loading…</div></DashboardPage>`), so
// the canvas does not flash white while the figures load.
// src/__tests__/dashboardPageShell.test.tsx fails when a dashboard route's
// component paints `an-home` without this shell.
import type { CSSProperties, ReactNode } from "react";
import {
  useDocumentModeActive,
  useDocumentModeCanvasWide,
} from "./DocumentModeBoundary";

import { LISTING_GUTTER } from "../utils/dataGridSx";

import "./aeronet-home.css";

/**
 * The LISTING edge: `.an-home` keeps its top and bottom (28px / 80px) but takes
 * its sides from `LISTING_GUTTER`, the constant a DataGrid's rows sit on
 * (PATTERNS.md BP-72). Aaron 2026-09-28, ruling the Flight & Duty follow-up:
 * "Yes, match the list views" — a board page's (+) and banners start where the
 * List mode's first column does. Owned by the design layer; the F&D pages
 * take it through flight-duty/components/fdPage.ts (`FD_PAGE_STYLE`).
 */
export const LISTING_PAGE_SIDES: CSSProperties = {
  maxWidth: "100%",
  paddingLeft: LISTING_GUTTER,
  paddingRight: LISTING_GUTTER,
};

/**
 * A KPI strip ABOVE a listing grid (Bookings › Contracts): the LISTING edge,
 * and no bottom padding, because the grid's own toolbar supplies the gap
 * beneath it. Put it on an `.an-home` element that holds only the strip (and
 * its warn strip); the grid renders after it, outside `.an-home`.
 */
export const LISTING_STRIP_STYLE: CSSProperties = {
  ...LISTING_PAGE_SIDES,
  paddingBottom: 0,
};

export interface DashboardPageProps {
  children?: ReactNode;
  /** Forwarded to the `.an-home` element, for tests that find the page. */
  "data-testid"?: string;
}

// No gutter variant: its one consumer, the Flight & Duty Dashboard mode, became
// a Pilots-home tab (H287) and draws inside SectionDashboard's shell.
const DashboardPage = ({
  children,
  "data-testid": testId,
}: DashboardPageProps) => {
  const record = useDocumentModeActive();
  const wide = useDocumentModeCanvasWide();
  return (
    <DashboardCanvas>
      <div
        className="an-home"
        data-testid={testId}
        style={
          record
            ? { padding: 0, maxWidth: "none" }
            : wide
            ? { maxWidth: "none" }
            : undefined
        }
      >
        {children}
      </div>
    </DashboardCanvas>
  );
};

/**
 * The canvas alone, with no `.an-home` root: the neutral shell a route shows
 * while it is still deciding WHICH page to render (SectionDashboard waiting
 * for the user to resolve its persona home). Rendering `.an-home` there would
 * start a page that may be swapped a moment later.
 */
export const DashboardCanvas = ({ children }: { children?: ReactNode }) => {
  const documentCanvas = useDocumentModeCanvasWide();
  return (
    <div
      className="an-home-bg"
      data-an-dashboard-page=""
      style={documentCanvas ? { minHeight: "calc(100dvh - 70px)" } : undefined}
    >
      {children}
    </div>
  );
};

export default DashboardPage;
